import type {
  BookingStatus, CategoryField, PricingType, ProductStatus, RentalCategory,
} from "../types/yeatiLoc.types";

// ============ DEBOUNCE ============

export const YEATI_LOC_DEBOUNCE_DELAY = 500;

// ============ CARD BACKGROUND ============

export const CARD_BACKGROUND_IMAGE =
  "https://framerusercontent.com/images/Xoud3vbcR1ZHKeXiqkEcqYoaDk.jpg?width=325&height=238";

// ============ STATUS OPTIONS ============

export const PRODUCT_STATUS_OPTIONS: Array<{
  value: ProductStatus
  label: string
  color: string
}> = [
  { value: "draft", label: "Brouillon", color: "bg-slate-100 text-slate-600" },
  { value: "published", label: "Publié", color: "bg-emerald-50 text-emerald-700" },
  { value: "archived", label: "Archivé", color: "bg-amber-50 text-amber-700" },
];

export const BOOKING_STATUS_OPTIONS: Array<{
  value: BookingStatus
  label: string
  color: string
}> = [
  { value: "cart", label: "Panier", color: "bg-slate-100 text-slate-600" },
  { value: "pending_checkout", label: "Paiement en cours", color: "bg-cyan-50 text-cyan-700" },
  { value: "pending", label: "En attente", color: "bg-amber-50 text-amber-700" },
  { value: "approved", label: "Validée - à régler", color: "bg-sky-50 text-sky-700" },
  { value: "confirmed", label: "Confirmée", color: "bg-blue-50 text-blue-700" },
  { value: "in_progress", label: "En cours", color: "bg-violet-50 text-violet-700" },
  { value: "pending_return", label: "Retour attendu", color: "bg-orange-50 text-orange-700" },
  { value: "completed", label: "Terminée", color: "bg-emerald-50 text-emerald-700" },
  { value: "ristourne", label: "Ristournée", color: "bg-slate-50 text-slate-700" },
  { value: "rejected", label: "Refusée", color: "bg-rose-50 text-rose-700" },
  { value: "cancelled", label: "Annulée", color: "bg-red-50 text-red-700" },
];

export const PRICING_TYPE_OPTIONS = [
  { value: "per_night" as const, label: "Par nuit" },
  { value: "per_week" as const, label: "Par semaine" },
  { value: "per_day" as const, label: "Par jour" },
  { value: "fixed" as const, label: "Prix fixe" },
  { value: "period_total" as const, label: "Forfait par séjour" },
  { value: "duration_tier" as const, label: "Selon la durée" },
];

// Per-category allowed pricing types. Hébergement supporte deux
// modèles : forfaitaire (`period_total`, l'élu pose des séjours figés)
// et saisonnier (`per_night`, l'élu pose des saisons et le bénéf
// choisit ses dates librement).
export const PRICING_TYPES_BY_CATEGORY: Record<string, PricingType[]> = {
  cat_hebergement: ["period_total", "per_night"],
  cat_emplacement: ["per_night", "per_week", "fixed", "duration_tier"],
  cat_materiel: ["per_day", "per_week", "fixed", "duration_tier"],
  cat_vehicule: ["per_day", "per_week", "fixed", "duration_tier"],
  cat_salle: ["per_day", "fixed", "duration_tier"],
  // Livre : prêt médiathèque, modèle simple - un prix fixe (0€ par
  // défaut pour un prêt gratuit). Pas de saisonnalité ni de tarif
  // au jour : si le CSE veut un autre modèle, il sortira du wizard
  // « livre » et créera un item matériel.
  cat_livre: ["fixed"],
};

// Introduction du bloc Tarification d'un hébergement : ses tarifs
// (variantes) se saisissent dans la même grille que les périodes.
export const HEBERGEMENT_PRICING_INTRO = "Fixez le prix de chaque période. Vous pouvez proposer plusieurs tarifs pour ce même logement (par exemple selon la situation du salarié) : il choisit le sien en réservant, et une réservation bloque les dates pour tous les tarifs.";

export const DEFAULT_PRICING_TYPE_BY_CATEGORY: Record<string, PricingType> = {
  cat_hebergement: "per_night",
  cat_emplacement: "per_night",
  cat_materiel: "per_day",
  cat_vehicule: "per_day",
  cat_salle: "per_day",
  cat_livre: "fixed",
};

// Whether the category defaults to seasonal (multi-period) pricing.
// Hebergement/Emplacement: high vs low season is the norm.
// Materiel/Vehicule/Salle/Livre: typically a flat year-round price.
export const DEFAULT_SEASONAL_BY_CATEGORY: Record<string, boolean> = {
  cat_hebergement: true,
  cat_emplacement: true,
  cat_materiel: false,
  cat_vehicule: false,
  cat_salle: false,
  cat_livre: false,
};

// ============ WIZARD ============

export const WIZARD_STEPS = [
  {
    step: 1, label: "Type", description: "Choisissez le type de produit", icon: "LayoutGrid",
  },
  {
    step: 2, label: "Informations", description: "Décrivez votre produit", icon: "FileText",
  },
  {
    step: 3, label: "Paramètres", description: "Configurez les options", icon: "Settings",
  },
  {
    step: 4, label: "Tarification", description: "Définissez vos prix", icon: "Euro",
  },
  {
    step: 5, label: "Récapitulatif", description: "Vérifiez et publiez", icon: "CheckCircle",
  },
] as const;

// ============ MESSAGES ============

export const YEATI_LOC_MESSAGES = {
  product: {
    createSuccess: "Produit créé avec succès",
    createError: "Erreur lors de la création du produit",
    updateSuccess: "Produit mis à jour",
    updateError: "Erreur lors de la mise à jour",
    deleteSuccess: "Produit supprimé",
    deleteError: "Erreur lors de la suppression",
    duplicateSuccess: "Produit dupliqué : la copie est en brouillon",
    duplicateError: "Erreur lors de la duplication du produit",
    publishSuccess: "Produit publié",
    archiveSuccess: "Produit archivé",
  },
  booking: {
    createSuccess: "Réservation créée avec succès",
    createError: "Erreur lors de la création de la réservation",
    updateSuccess: "Réservation mise à jour",
    updateError: "Erreur lors de la mise à jour",
    confirmSuccess: "Réservation confirmée",
    cancelSuccess: "Réservation annulée",
    approveSuccess: "Demande approuvée",
    rejectSuccess: "Demande refusée",
    // Mêmes libellés que l'export des commandes boutique (ECOM_ORDERS_MESSAGES).
    exportSuccess: "Export XLSX démarré",
    exportError: "Erreur lors de l'export",
  },
} as const;

// ============ CATEGORIES ============

const HEBERGEMENT_FIELDS: CategoryField[] = [
  {
    name: "capacity",
    label: "Capacité d'accueil",
    type: "number",
    required: true,
    placeholder: "Nombre de personnes",
    helpText: "Nombre maximum de personnes pouvant séjourner",
  },
  {
    name: "bedrooms",
    label: "Nombre de chambres",
    type: "number",
    required: true,
    placeholder: "Ex: 2",
  },
  {
    name: "surface",
    label: "Surface (m2)",
    type: "number",
    required: false,
    placeholder: "Ex: 45",
  },
  {
    name: "location",
    label: "Localisation",
    type: "text",
    required: true,
    placeholder: "Ex: Île de Ré, Charente-Maritime",
  },
  {
    name: "amenities",
    label: "Équipements",
    // `tags` : liste éditable item-by-item. `options` sert de
    // valeurs par défaut au moment de la création (l'élu démarre
    // avec la canonical list et peut ajouter/retirer librement).
    type: "tags",
    required: false,
    options: ["Piscine", "Parking", "Wi-Fi", "Climatisation", "Terrasse", "Barbecue", "Lave-linge"],
    helpText: "Personnalisez la liste des équipements disponibles dans cet hébergement",
  },
  {
    name: "pets_allowed",
    label: "Animaux acceptés",
    type: "boolean",
    required: false,
    defaultValue: false,
  },
];

const MATERIEL_FIELDS: CategoryField[] = [
  // Note : on n'expose plus de field `stock` ici. Le trio Mode/Gestion
  // du stock/Validation au début de Step3 + le champ « Stock disponible »
  // dans ProductEditForm (conditionné par stock_strategy === 'quantity'
  // et masqué si variantes) couvrent déjà le besoin de façon
  // cross-catégorie. Doublonner ici créait 2 champs « Stock disponible »
  // visibles côté wizard, dont 1 muet dès qu'une variante existait.
  {
    name: "brand",
    label: "Marque",
    type: "text",
    required: false,
    placeholder: "Ex: Bosch, Makita",
  },
  {
    name: "condition",
    label: "État",
    type: "select",
    required: true,
    options: ["Neuf", "Très bon état", "Bon état", "État correct"],
  },
  {
    name: "requires_deposit",
    label: "Caution obligatoire",
    type: "boolean",
    required: false,
    defaultValue: true,
  },
];

const EMPLACEMENT_FIELDS: CategoryField[] = [
  {
    name: "surface",
    label: "Surface (m2)",
    type: "number",
    required: true,
    placeholder: "Ex: 100",
  },
  {
    name: "location",
    label: "Localisation",
    type: "text",
    required: true,
    placeholder: "Ex: Camping Les Flots Bleus",
  },
  {
    name: "electricity",
    label: "Électricité incluse",
    type: "boolean",
    required: false,
    defaultValue: false,
  },
  {
    name: "water",
    label: "Eau incluse",
    type: "boolean",
    required: false,
    defaultValue: false,
  },
  {
    name: "max_vehicles",
    label: "Nombre max de véhicules",
    type: "number",
    required: false,
    placeholder: "Ex: 1",
  },
];

const VEHICULE_FIELDS: CategoryField[] = [
  {
    name: "vehicle_type",
    label: "Type de véhicule",
    type: "select",
    required: true,
    options: ["Voiture", "Utilitaire", "Minibus", "Vélo", "Trottinette"],
  },
  {
    name: "seats",
    label: "Nombre de places",
    type: "number",
    required: true,
    placeholder: "Ex: 5",
  },
  {
    name: "license_required",
    label: "Permis requis",
    type: "boolean",
    required: false,
    defaultValue: true,
  },
  {
    name: "mileage_limit",
    label: "Kilométrage limite (km/jour)",
    type: "number",
    required: false,
    placeholder: "Ex: 200",
    helpText: "Laissez vide pour kilométrage illimité",
  },
];

const SALLE_FIELDS: CategoryField[] = [
  {
    name: "capacity",
    label: "Capacité d'accueil",
    type: "number",
    required: true,
    placeholder: "Nombre de personnes",
  },
  {
    name: "surface",
    label: "Surface (m2)",
    type: "number",
    required: true,
    placeholder: "Ex: 80",
  },
  {
    name: "location",
    label: "Adresse",
    type: "text",
    required: true,
    placeholder: "Ex: 12 rue de la Paix, Paris",
  },
  {
    name: "equipment",
    label: "Équipements",
    type: "multiselect",
    required: false,
    options: ["Vidéoprojecteur", "Écran", "Sono", "Micro", "Tables", "Chaises", "Cuisine"],
  },
  {
    name: "catering_allowed",
    label: "Traiteur autorisé",
    type: "boolean",
    required: false,
    defaultValue: true,
  },
];

// Livre : usage médiathèque CSE. Les méta (auteur / éditeur / ISBN /
// pages) sont auto-injectées via ISBNdb dans la description
// HTML - pas la peine de re-saisir des champs settings dédiés. Si
// jamais l'élu veut surcharger, il édite directement le rich-text.
const LIVRE_FIELDS: CategoryField[] = [];

// Voyage : pas de champ descriptif dédié, le programme vit dans la
// description. Les places sont portées par les départs (créneaux), les prix
// par la grille catégorie × tranche.
const VOYAGE_FIELDS: CategoryField[] = [];

export const DEFAULT_CATEGORIES: RentalCategory[] = [
  {
    id: "cat_hebergement",
    name: "Hébergement",
    icon: "Home",
    color: "#3b82f6",
    description: "Mobil-homes, gîtes, appartements et maisons de vacances",
    fields: HEBERGEMENT_FIELDS,
    enabled: true,
  },
  {
    id: "cat_materiel",
    name: "Matériel",
    icon: "Wrench",
    color: "#f59e0b",
    description: "Outils, équipements sportifs et matériel divers",
    fields: MATERIEL_FIELDS,
    enabled: true,
  },
  {
    id: "cat_livre",
    name: "Livre",
    icon: "BookOpen",
    color: "#a855f7",
    description: "Médiathèque CSE - prêt de livres avec recherche ISBNdb",
    fields: LIVRE_FIELDS,
    enabled: true,
  },
  {
    id: "cat_voyage",
    name: "Voyage",
    icon: "Plane",
    color: "#0ea5e9",
    description: "Séjours et circuits à dates fixes, prix par catégorie de voyageur et par tranche",
    fields: VOYAGE_FIELDS,
    enabled: true,
  },
  // Catégories désactivées pour le MVP : modèle de réservation pas encore
  // câblé côté backend pour ces kinds (validations spécifiques, pricing
  // engine, etc.). Conservées dans la liste pour que les items legacy
  // qui les portent encore (sur d'autres orgs ou via seeds) puissent
  // toujours être rendus avec le bon icône/label, mais filtrées dans
  // les pickers de création / chips catalogue / filtres planning.
  {
    id: "cat_emplacement",
    name: "Emplacement",
    icon: "MapPin",
    color: "#10b981",
    description: "Emplacements de camping, parcelles et terrains",
    fields: EMPLACEMENT_FIELDS,
    enabled: false,
  },
  {
    id: "cat_vehicule",
    name: "Véhicule",
    icon: "Car",
    color: "#8b5cf6",
    description: "Voitures, utilitaires, vélos et trottinettes",
    fields: VEHICULE_FIELDS,
    enabled: false,
  },
  {
    id: "cat_salle",
    name: "Salle",
    icon: "Building2",
    color: "#ec4899",
    description: "Salles de réunion, de fête et espaces événementiels",
    fields: SALLE_FIELDS,
    enabled: false,
  },
];

/** Catégories ouvertes à la création - filtrées dans les pickers
 * (collection form, chips catalogue, filtre planning). Pour le lookup
 * d'un kind existant (legacy / autres orgs), continuer à utiliser
 * `DEFAULT_CATEGORIES`. */
export const ENABLED_CATEGORIES: RentalCategory[] = DEFAULT_CATEGORIES.filter(
  (c) => c.enabled,
);
